import ButtonBase from '@mui/material/ButtonBase';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import { useTheme, alpha } from '@mui/material/styles';

export type ToggleStatusPillProps = {
  value: boolean;
  uploadedLabel?: string;
  pendingLabel?: string;
  onClick?: () => void;
  ariaLabel?: string;
  disabled?: boolean;
};

export const ToggleStatusPill = ({
  value,
  uploadedLabel = 'Uploaded',
  pendingLabel = 'Not uploaded',
  onClick,
  ariaLabel,
  disabled,
}: ToggleStatusPillProps) => {
  const theme = useTheme();
  const tokens = value
    ? {
        bg: theme.palette.success.light,
        fg: theme.palette.success.main,
        hoverBg: alpha(theme.palette.success.main, 0.18),
      }
    : {
        bg: theme.palette.warning.light,
        fg: theme.palette.warning.main,
        hoverBg: alpha(theme.palette.warning.main, 0.18),
      };

  const Icon = value ? CheckCircleIcon : RadioButtonUncheckedIcon;
  const label = value ? uploadedLabel : pendingLabel;
  const interactive = !disabled && Boolean(onClick);

  const tooltipText = disabled
    ? ''
    : value
      ? `Click to mark as ${pendingLabel.toLowerCase()}`
      : `Click to mark as ${uploadedLabel.toLowerCase()}`;

  const body = (
    <ButtonBase
      onClick={interactive ? onClick : undefined}
      disabled={!interactive}
      aria-label={ariaLabel ?? label}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        py: 0.625,
        pl: 1.25,
        pr: 1.5,
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        background: tokens.bg,
        color: tokens.fg,
        border: 0,
        cursor: interactive ? 'pointer' : 'default',
        transition: 'background 0.12s ease',
        whiteSpace: 'nowrap',
        '&:hover': interactive ? { background: tokens.hoverBg } : undefined,
        '&.Mui-disabled': { color: tokens.fg, opacity: 1 },
      }}
    >
      <Icon sx={{ fontSize: 16 }} />
      {label}
    </ButtonBase>
  );

  if (!interactive) {
    return body;
  }
  return (
    <Tooltip title={tooltipText} arrow>
      <Box component="span" sx={{ display: 'inline-flex' }}>{body}</Box>
    </Tooltip>
  );
};
